import React from "react";
import { Link } from "react-router-dom";
import mixpanel from "@/lib/mixpanel";

interface Item {
  id: string | number;
  title: string;
  account_id: string | undefined;
  report_ids?: string[];
  created_at: number;
  updated_at: number;
  pinned?: boolean;
}

interface ItemGroup<T> {
  title: string;
  items: T[];
}

interface GroupedItemsListProps<T extends Item> {
  items: T[];
  renderItem: (item: T, isActive?: boolean) => React.ReactNode;
  viewAllLink: string;
  viewAllText: string;
  activeItemId?: string;
  viewAllTrackingName?: string;
}

const groupItemsByDate = <T extends Item>(items: T[]): ItemGroup<T>[] => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const sevenDaysAgo = new Date(startOfToday);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const thirtyDaysAgo = new Date(startOfToday);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // Separate pinned and unpinned items
  const pinnedItems = items.filter((item) => item.pinned);
  const unpinnedItems = items.filter((item) => !item.pinned);

  return [
    {
      title: "Pinned",
      items: pinnedItems,
    },
    {
      title: "Today",
      items: unpinnedItems.filter((item) => {
        const itemDate = new Date(item.created_at);
        return itemDate >= startOfToday;
      }),
    },
    {
      title: "Previous 7 Days",
      items: unpinnedItems.filter((item) => {
        const itemDate = new Date(item.created_at);
        return itemDate >= sevenDaysAgo && itemDate < startOfToday;
      }),
    },
    {
      title: "Previous 30 Days",
      items: unpinnedItems.filter((item) => {
        const itemDate = new Date(item.created_at);
        return itemDate >= thirtyDaysAgo && itemDate < sevenDaysAgo;
      }),
    },
  ];
};

// Some item lists have numbers as IDs, others have UUIDs
function flexibleIdCompare(
  testId: string | number,
  activeItemId: string | number,
) {
  if (typeof testId === "number") {
    return testId === parseInt(activeItemId as string);
  } else {
    return testId === activeItemId;
  }
}

export const GroupedItemsList = <T extends Item>({
  items,
  renderItem,
  viewAllLink,
  viewAllText,
  activeItemId,
  viewAllTrackingName,
}: GroupedItemsListProps<T>) => {
  const groupedItems = groupItemsByDate(items);

  const handleViewAllClick = () => {
    if (viewAllTrackingName) {
      mixpanel.track("Click", { eventDescription: viewAllTrackingName });
    }
  };

  return (
    <>
      {groupedItems.map(
        (group) =>
          group.items.length > 0 && (
            <div key={group.title}>
              <div className="px-2 py-1 text-sm text-gray-700 font-bold mb-2 dark:text-white">
                {group.title}
              </div>
              <div className="mb-4">
                {group.items.map((item) => {
                  const isActive = activeItemId
                    ? flexibleIdCompare(item.id, activeItemId)
                    : false;
                  return renderItem(item, isActive);
                })}
              </div>
            </div>
          ),
      )}
      {items.length >= 10 && (
        <Link to={viewAllLink} onClick={handleViewAllClick}>
          <div className="py-1.5 px-2 text-[var(--sidebar-active-color)] underline font-medium">
            {viewAllText}
          </div>
        </Link>
      )}
    </>
  );
};
